import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Lecture des refus de la requête **`activityLog`** (PCG-337), le journal d'audit du
 * back-office.
 *
 * Septième vocabulaire à côté de `accounts/portalAccess.ts`, `moduleRefusal.ts`,
 * `catalogues/refusals.ts`, `onboarding/inviteRefusal.ts`, `users/resendRefusal.ts` et
 * `accounts/accountsRefusal.ts`, réutilisant `refusalCode` et `Refusal` : **comment** un
 * code se lit sur une erreur Apollo est un fait de l'API, pas de cet écran.
 *
 * **MÊME PORTE que `accounts`**, le SDL le dit mot pour mot : un rôle back-office et le
 * droit `manage_accounts`, borné par le périmètre. Les codes sont donc les mêmes que
 * ceux d'`accountsRefusalOf`, et les phrases suivent, un journal se demandant comme une
 * liste. La branche générique reste pour un code que l'écran ne sait pas encore lire.
 *
 * **Attention, un périmètre vide n'est pas ici**, exactement comme pour `accounts` : un
 * opérateur sans affectation reçoit une **page vide**, total zéro, et non un refus. Lu
 * comme un échec il enverrait demander un droit déjà détenu ; l'écran le rend en vide.
 *
 * **Une date illisible EST un refus** (`from`/`to` mal formés) : le SDL écrit « une date
 * illisible est refusée, pas ignorée ». L'écran ne construit `from` que depuis ses
 * propres préréglages, donc il ne l'atteint pas ; la branche générique le couvre si
 * l'API en change un jour la forme.
 */
export const journalRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'MISSING_PERMISSION':
            return {
                title: c('Activity log refusal').t`Your role does not let you read the activity log.`,
                guidance: c('Activity log refusal')
                    .t`The audit log is gated by the same permission as the accounts list, so that nobody lands on a screen where everything refuses. The log was refused, not answered: this is not an empty history. Ask an administrator for that permission.`,
            };
        case 'INTERNAL_STAFF_ONLY':
            return {
                title: c('Activity log refusal').t`This screen belongs to the back-office.`,
                guidance: c('Activity log refusal')
                    .t`Reading the trace of administration gestures is a Freelance.com matter, so a client-side role cannot carry it whatever its permissions. The scope of the role is checked before the permission, which is why a custom role holding the right is refused too.`,
            };
        case 'NO_ACTIVE_MEMBERSHIP':
            return {
                title: c('Activity log refusal').t`Your session carries no active membership.`,
                guidance: c('Activity log refusal')
                    .t`Your identity is recognised but it is attached to nothing right now, so there is no perimeter to read the log through. Sign in again; if it happens once more, your own access has to be reopened.`,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Activity log refusal').t`Your session is no longer valid.`,
                guidance: c('Activity log refusal').t`Sign in again, then open this log back.`,
            };
        default:
            return {
                title: c('Activity log refusal').t`The activity log could not be read.`,
                guidance: c('Activity log refusal')
                    .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. Above all it does not say the history is empty: nothing was read. The server's answer is below; it is what the support desk needs.`,
                // `String(error)` sur une erreur inconnue plutôt qu'un blanc : un bloc
                // technique vide se lit comme un écran qui a avalé la réponse.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
